import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { AnimatedBrandLogo } from '@/components/animated-brand-logo';
import { authClient } from '@/lib/auth-client';
import { resolveUserFirstName } from '@/lib/user-display-name';
import type { ChatSessionIntent } from '../chat-session-intent';
import {
  playChatWelcomeCompose,
  resetChatWelcomeCompose,
  setChatWelcomeComposeFinalState,
} from './chat-welcome-compose';

type ChatWelcomeProps = {
  intent?: ChatSessionIntent;
};

const DOCUMENT_SEARCH_WELCOME_LINES = [
  ['Recherchez', 'dans', 'votre'],
  ['base', 'documentaire'],
] as const;

function buildGeneralWelcomeLines(firstName: string | null): readonly (readonly string[])[] {
  if (firstName) {
    return [
      ['Ravi', 'de', 'vous', 'voir,'],
      [`${firstName}.`],
    ];
  }

  return [['Ravi', 'de', 'vous', 'voir.']];
}

export function ChatWelcome({ intent = 'general' }: ChatWelcomeProps) {
  const sessionState = authClient.useSession();
  const firstName = useMemo(
    () => resolveUserFirstName(sessionState.data?.user.name),
    [sessionState.data?.user.name],
  );
  const titleLines = useMemo(
    () =>
      intent === 'document-search'
        ? DOCUMENT_SEARCH_WELCOME_LINES
        : buildGeneralWelcomeLines(firstName),
    [firstName, intent],
  );

  const stackRef = useRef<HTMLDivElement>(null);
  const titleShellRef = useRef<HTMLDivElement>(null);
  const composeTimelineRef = useRef<gsap.core.Timeline | null>(null);
  const [prevIntent, setPrevIntent] = useState(intent);
  const [isLogoRevealComplete, setIsLogoRevealComplete] = useState(false);

  if (intent !== prevIntent) {
    setPrevIntent(intent);
    setIsLogoRevealComplete(false);
  }

  useLayoutEffect(() => {
    if (!isLogoRevealComplete) {
      return;
    }

    const stack = stackRef.current;
    const titleShell = titleShellRef.current;
    if (!stack || !titleShell) {
      return;
    }

    const words = Array.from(titleShell.querySelectorAll<HTMLElement>('.chat-welcome-word'));
    const elements = { stack, titleShell, words };
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (reducedMotion) {
      setChatWelcomeComposeFinalState(elements);
      return;
    }

    playChatWelcomeCompose(elements, composeTimelineRef);

    return () => {
      composeTimelineRef.current?.kill();
      composeTimelineRef.current = null;
    };
  }, [intent, isLogoRevealComplete, titleLines]);

  useLayoutEffect(() => {
    const stack = stackRef.current;
    const titleShell = titleShellRef.current;
    if (!stack || !titleShell) {
      return;
    }

    if (isLogoRevealComplete) {
      return;
    }

    const words = Array.from(titleShell.querySelectorAll<HTMLElement>('.chat-welcome-word'));
    resetChatWelcomeCompose({ stack, titleShell, words });

    return () => {
      composeTimelineRef.current?.kill();
      composeTimelineRef.current = null;
    };
  }, [intent, isLogoRevealComplete, titleLines]);

  const handleLogoRevealComplete = () => {
    setIsLogoRevealComplete(true);
  };

  return (
    <section className="chat-welcome mx-auto w-full max-w-2xl text-center">
      <div ref={stackRef} className="chat-welcome__stack flex w-full flex-col items-center">
        <div className="chat-welcome__logo w-full max-w-[11rem] md:max-w-[13rem]">
          <AnimatedBrandLogo
            replayKey={intent}
            variant="hero"
            className="mb-0 max-w-full"
            onRevealComplete={handleLogoRevealComplete}
          />
        </div>

        <div ref={titleShellRef} className="chat-welcome__title-shell w-full">
          <h1 className="chat-welcome-title w-full text-[1.5rem] font-medium leading-tight tracking-[-0.03em] text-foreground md:text-[2rem] md:leading-[2.35rem]">
            {titleLines.map((line, lineIndex) => (
              <span key={`${intent}-line-${lineIndex}`} className="chat-welcome-title-line">
                {line.map((word, wordIndex) => (
                  <span
                    key={`${intent}-line-${lineIndex}-word-${wordIndex}`}
                    className="chat-welcome-word"
                  >
                    {word}
                  </span>
                ))}
              </span>
            ))}
          </h1>
        </div>
      </div>
    </section>
  );
}
